(() => {
  const elementName = 'kl-pdp-story';

  if (customElements.get(elementName)) return;

  class KlPdpStory extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayRequested = this.dataset.autoplay === 'true';
      this.duration = Math.max(3, Number(this.dataset.duration) || 8) * 1000;
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
      this.userPaused = false;
      this.interactiveHolds = new Set();
      this.activeIndex = 0;
      this.programmaticUntil = 0;
      this.scrollFrame = null;
      this.timer = null;
      this.idleTimer = null;
      this.layoutTimer = null;
      this.goToTimer = null;
      this.progressAnimation = null;

      this.panels = Array.from(this.querySelectorAll('[data-kl-panel]'));
      this.nav = this.querySelector('[data-kl-nav]');
      this.navButtons = Array.from(this.querySelectorAll('[data-kl-nav-button]'));
      this.autoplayToggle = this.querySelector('[data-kl-autoplay-toggle]');
      this.productTarget = this.findProductTarget();
      this.targets = this.productTarget ? [...this.panels, this.productTarget] : [...this.panels];

      if (!this.productTarget && this.navButtons.length > this.panels.length) {
        this.navButtons[this.navButtons.length - 1].closest('li').hidden = true;
      }

      this.updateHeaderOffset();
      this.setupEditorialSticky();
      this.bindNavigation();
      this.setupHeroCart();
      this.bindPageEvents();
      this.bindInteractiveAreas();
      this.setupCarousels();
      this.syncAutoplayControl();
      this.classList.add('is-ready');
      this.updateActiveFromViewport(true);
      this.syncVideos();
      this.layoutTimer = window.setTimeout(() => {
        this.updateHeaderOffset();
        this.updateActiveFromViewport(true);
      }, 750);
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.clearTimer();
      this.clearCarouselTimers();
      window.clearTimeout(this.idleTimer);
      window.clearTimeout(this.layoutTimer);
      window.clearTimeout(this.goToTimer);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.abortController.abort();
      this.stickyResizeObserver?.disconnect();
      this.releaseEditorialSticky();
      this.interactiveHolds.clear();
      document.body.classList.remove('kl-pdp-story-active');
      this.classList.remove('is-ready');
      this.initialized = false;
    }

    findProductTarget() {
      const sectionElement = this.closest('.shopify-section');
      let sibling = sectionElement ? sectionElement.nextElementSibling : null;

      while (sibling) {
        const productInfo = sibling.matches('product-info[id^="MainProduct-"]')
          ? sibling
          : sibling.querySelector('product-info[id^="MainProduct-"]');
        if (productInfo) return productInfo;
        sibling = sibling.nextElementSibling;
      }

      return document.querySelector('main#MainContent product-info[id^="MainProduct-"]');
    }

    setupEditorialSticky() {
      this.stickyHero = this.querySelector('.kl-pdp-story__panel--hero');
      this.editorialStickyEnabled = this.dataset.earlySticky === 'true' && Boolean(this.stickyHero);
      if (!this.editorialStickyEnabled) return;

      const refresh = () => this.updateEditorialSticky();
      if ('ResizeObserver' in window) {
        this.stickyResizeObserver = new ResizeObserver(refresh);
        this.stickyResizeObserver.observe(this.stickyHero);
      }
      document.addEventListener('shopify:section:load', refresh, { signal: this.signal });
      window.addEventListener('load', refresh, { signal: this.signal });
    }

    releaseEditorialSticky() {
      this.stickyBar?.classList.remove('kl-pdp-story-sticky', 'kl-pdp-story-sticky--visible');
      this.stickyBar = null;
    }

    updateEditorialSticky() {
      if (!this.editorialStickyEnabled) return;

      // Resolve fresh nodes after theme-editor section reloads or variant re-renders.
      const mainProduct = this.findProductTarget();
      const bar = document.querySelector('.sticky-atc-bar');
      if (!mainProduct || !bar || !this.stickyHero.isConnected) {
        this.releaseEditorialSticky();
        return;
      }
      if (mainProduct !== this.stickyProductTarget) {
        if (this.stickyProductTarget) this.stickyResizeObserver?.unobserve(this.stickyProductTarget);
        this.stickyProductTarget = mainProduct;
        this.stickyResizeObserver?.observe(mainProduct);
      }
      if (bar !== this.stickyBar) {
        this.releaseEditorialSticky();
        this.stickyBar = bar;
        bar.classList.add('kl-pdp-story-sticky');
      }

      const heroRect = this.stickyHero.getBoundingClientRect();
      const passedHero = heroRect.height > 0 && heroRect.bottom <= this.headerOffset;
      const mainButton = mainProduct.querySelector('.main-product-form [name="add"]');
      const buttonRect = mainButton?.getBoundingClientRect();
      const buttonVisible = Boolean(
        buttonRect && buttonRect.width > 0 && buttonRect.height > 0 &&
        buttonRect.bottom > this.headerOffset && buttonRect.top < window.innerHeight &&
        window.getComputedStyle(mainButton).visibility !== 'hidden'
      );
      bar.classList.toggle('kl-pdp-story-sticky--visible', passedHero && !buttonVisible);
    }

    bindNavigation() {
      this.navButtons.forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            const index = Number(button.dataset.klIndex);
            if (Number.isInteger(index) && this.targets[index]) this.goTo(index);
          },
          { signal: this.signal }
        );
      });

      this.querySelectorAll('[data-kl-product-link]').forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            if (this.productTarget) this.goTo(this.targets.length - 1);
          },
          { signal: this.signal }
        );
      });

      if (this.autoplayToggle) {
        this.autoplayToggle.addEventListener(
          'click',
          () => {
            this.userPaused = !this.userPaused;
            this.autoplayToggle.classList.toggle('is-paused', this.userPaused);
            this.autoplayToggle.setAttribute(
              'aria-label',
              this.userPaused
                ? this.autoplayToggle.dataset.playLabel
                : this.autoplayToggle.dataset.pauseLabel
            );

            this.syncAutoMotion();
            this.syncVideos();
          },
          { signal: this.signal }
        );
      }
    }

    setupHeroCart() {
      this.heroCartButton = this.querySelector('[data-kl-hero-cart]');
      if (!this.heroCartButton) return;

      this.heroCartError = this.querySelector('[data-kl-cart-error]');
      this.heroCartPending = false;
      this.heroCartButton.addEventListener('click', () => this.addHeroCart(), { signal: this.signal });

      document.addEventListener('toggle', (event) => {
        if (!event.target.matches?.('cart-drawer')) return;
        if (event.target.hasAttribute('open')) this.holdAutomaticMotion('hero-cart-drawer');
        else this.releaseAutomaticMotion('hero-cart-drawer');
      }, { signal: this.signal });

      this.syncHeroCart();
    }

    syncHeroCart() {
      const busy = this.heroCartPending;
      const unavailable = this.heroCartButton.dataset.available !== 'true' ||
        !this.heroCartButton.dataset.variantId;
      this.heroCartButton.disabled = unavailable || busy;
      this.heroCartButton.setAttribute('aria-busy', String(busy));
      this.heroCartButton.textContent = busy
        ? this.heroCartButton.dataset.loadingLabel
        : unavailable
          ? this.heroCartButton.dataset.unavailableLabel
          : this.heroCartButton.dataset.label;

      if (busy) this.holdAutomaticMotion('hero-cart-pending');
      else if (this.interactiveHolds.has('hero-cart-pending')) {
        this.releaseAutomaticMotion('hero-cart-pending');
      }
    }

    async addHeroCart() {
      this.syncHeroCart();
      if (this.heroCartButton.disabled) return;

      const button = this.heroCartButton;
      this.heroCartPending = true;
      this.heroCartError.textContent = '';
      this.heroCartError.hidden = true;
      this.syncHeroCart();
      let added = false;
      let addErrorMessage = button.dataset.errorLabel;

      try {
        const requestedSections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', {
          bubbles: true, detail: { sections: requestedSections },
        }));
        const sections = [...new Set(requestedSections)].filter(Boolean).slice(0, 5);
        const payload = {
          id: button.dataset.variantId,
          quantity: Math.max(1, Number(button.dataset.quantity) || 1),
          sections_url: window.location.pathname,
        };
        if (sections.length) payload.sections = sections;

        // Use the section's product selection, never an unrelated main product form.
        const response = await fetch(button.dataset.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const productAdded = await response.json();
        if (!response.ok || productAdded.status >= 400) {
          addErrorMessage = productAdded.description || productAdded.message || addErrorMessage;
          throw new Error(addErrorMessage);
        }
        // A subsequent drawer/refresh failure must not turn a confirmed add into an error.
        added = true;
        document.dispatchEvent(new CustomEvent('product-ajax:added', {
          detail: { product: productAdded },
        }));

        const theme = typeof FoxTheme !== 'undefined' ? FoxTheme : null;
        const drawer = document.querySelector('cart-drawer');
        if (!drawer?.show || !theme?.pubsub || theme.settings?.cartType === 'page' ||
            !sections.length || sections.some((id) => !productAdded.sections?.[id])) {
          window.location.assign(button.dataset.cartUrl);
          return;
        }

        // The native helper defaults to POST; cart JSON must be retrieved with GET.
        const cartResponse = await fetch(`${button.dataset.cartUrl}.js`, {
          method: 'GET', credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error('Cart refresh failed');
        const cart = await cartResponse.json();
        cart.sections = productAdded.sections;
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
        drawer.show(button);
      } catch (error) {
        if (added) {
          // Never retry a successful add: the full cart is the safe UI fallback.
          window.location.assign(button.dataset.cartUrl);
        } else {
          this.heroCartError.textContent = addErrorMessage;
          this.heroCartError.hidden = false;
        }
      } finally {
        this.heroCartPending = false;
        this.syncHeroCart();
      }
    }

    bindPageEvents() {
      const requestUpdate = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateActiveFromViewport();
        });
      };

      window.addEventListener('scroll', requestUpdate, { passive: true, signal: this.signal });
      window.addEventListener(
        'resize',
        () => {
          this.updateHeaderOffset();
          this.updateActiveFromViewport(true);
          this.setupCarouselStates();
        },
        { passive: true, signal: this.signal }
      );

      ['wheel', 'touchstart'].forEach((eventName) => {
        window.addEventListener(eventName, () => this.registerUserIntent(), {
          passive: true,
          signal: this.signal,
        });
      });

      window.addEventListener(
        'keydown',
        (event) => {
          if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) {
            this.registerUserIntent();
          }
        },
        { signal: this.signal }
      );

      window.addEventListener(
        'blur',
        () => {
          this.clearTimer();
          this.clearCarouselTimers();
        },
        { signal: this.signal }
      );
      window.addEventListener(
        'focus',
        () => {
          this.syncAutoMotion();
        },
        { signal: this.signal }
      );
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.hidden) {
            this.clearTimer();
            this.clearCarouselTimers();
            this.pauseVideos();
          } else {
            this.updateActiveFromViewport(true);
            this.syncAutoMotion();
            this.syncVideos();
          }
        },
        { signal: this.signal }
      );

      document.addEventListener(
        'shopify:block:select',
        (event) => {
          const slide = event.target && event.target.closest
            ? event.target.closest('[data-kl-carousel-slide]')
            : null;
          if (!slide || !this.contains(slide)) return;

          this.clearAutoMotion();
          slide.scrollIntoView({
            behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        },
        { signal: this.signal }
      );

      this.reducedMotion.addEventListener(
        'change',
        () => {
          this.clearTimer();
          this.clearCarouselTimers();
          this.syncAutoplayControl();
          this.syncAutoMotion();
          this.syncVideos();
        },
        { signal: this.signal }
      );
    }

    bindInteractiveAreas() {
      const areas = Array.from(this.querySelectorAll('[data-kl-interactive], [data-kl-product-link]'));

      areas.forEach((area, index) => {
        const pointerKey = `area-${index}-pointer`;
        const focusKey = `area-${index}-focus`;
        const pauseOnHover = !area.matches('[data-kl-carousel]');

        if (pauseOnHover) {
          area.addEventListener(
            'pointerenter',
            () => this.holdAutomaticMotion(pointerKey),
            { signal: this.signal }
          );
          area.addEventListener(
            'pointerleave',
            () => this.releaseAutomaticMotion(pointerKey),
            { signal: this.signal }
          );
        }
        area.addEventListener(
          'focusin',
          () => this.holdAutomaticMotion(focusKey),
          { signal: this.signal }
        );
        area.addEventListener(
          'focusout',
          (event) => {
            if (area.contains(event.relatedTarget)) return;
            this.releaseAutomaticMotion(focusKey);
          },
          { signal: this.signal }
        );
      });
    }

    holdAutomaticMotion(key) {
      this.interactiveHolds.add(key);
      this.clearAutoMotion();
    }

    releaseAutomaticMotion(key) {
      this.interactiveHolds.delete(key);
      this.syncAutoMotion();
    }

    hasInteractiveHold() {
      return this.interactiveHolds.size > 0;
    }

    registerUserIntent() {
      if (performance.now() < this.programmaticUntil) return;

      this.clearAutoMotion();
      window.clearTimeout(this.idleTimer);
      this.idleTimer = window.setTimeout(() => this.syncAutoMotion(), 700);
    }

    updateHeaderOffset() {
      let offset = 0;
      const candidates = document.querySelectorAll(
        '.promopulse-top-banner, #countdown-contenedor, .shopify-section-group-header-group, .header-sticky, [data-sticky-header]'
      );

      candidates.forEach((element) => {
        const style = window.getComputedStyle(element);
        if (style.position !== 'sticky' && style.position !== 'fixed') return;

        const rect = element.getBoundingClientRect();
        if (rect.height > 0 && rect.top <= offset + 1 && rect.bottom > 0) {
          offset = Math.max(offset, rect.bottom);
        }
      });

      this.headerOffset = Math.max(0, Math.round(offset));
      this.style.setProperty('--kl-pdp-header-offset', `${this.headerOffset}px`);
      if (this.productTarget) this.productTarget.style.scrollMarginTop = `${this.headerOffset}px`;
    }

    updateActiveFromViewport(force = false) {
      this.updateEditorialSticky();
      if (!this.targets.length) return;

      const probe = this.headerOffset + (window.innerHeight - this.headerOffset) * 0.46;
      let nextIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.targets.forEach((target, index) => {
        const rect = target.getBoundingClientRect();
        if (rect.top <= probe && rect.bottom > probe) {
          nextIndex = index;
          closestDistance = 0;
          return;
        }

        if (closestDistance === 0) return;
        const distance = Math.min(Math.abs(rect.top - probe), Math.abs(rect.bottom - probe));
        if (distance < closestDistance) {
          closestDistance = distance;
          nextIndex = index;
        }
      });

      this.setActive(nextIndex, force);
    }

    setActive(index, force = false) {
      if (!this.targets[index]) return;
      if (!force && index === this.activeIndex) return;

      this.activeIndex = index;
      this.navButtons.forEach((button, buttonIndex) => {
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        if (active) {
          button.setAttribute('aria-current', 'step');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      const isProduct = index >= this.panels.length;
      this.nav.classList.toggle('is-hidden', isProduct);
      document.body.classList.toggle('kl-pdp-story-active', !isProduct);
      const tone = isProduct ? 'dark' : this.panels[index].dataset.klGuideTone || 'dark';
      this.nav.classList.toggle('is-light', tone === 'light');
      this.nav.classList.toggle('is-dark', tone !== 'light');

      this.syncVideos();
      this.syncAutoMotion();
    }

    goTo(index) {
      const target = this.targets[index];
      if (!target) return;

      this.programmaticUntil = performance.now() + 1200;
      this.setActive(index, true);
      target.scrollIntoView({
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });
      window.clearTimeout(this.goToTimer);
      this.goToTimer = window.setTimeout(() => {
        this.goToTimer = null;
        if (this.isConnected) this.updateActiveFromViewport(true);
      }, 1250);
    }

    scheduleAutoAdvance() {
      this.clearTimer();

      const canAdvance =
        this.autoplayRequested &&
        !this.designMode &&
        !this.reducedMotion.matches &&
        !this.userPaused &&
        !this.hasInteractiveHold() &&
        !document.hidden &&
        document.hasFocus() &&
        this.activeIndex < this.targets.length - 1 &&
        this.activeIndex < this.panels.length;

      if (!canAdvance) return;

      const progress = this.navButtons[this.activeIndex]?.querySelector(
        '.kl-pdp-story__nav-ring-progress'
      );
      if (progress && progress.animate) {
        this.progressAnimation = progress.animate(
          [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }],
          { duration: this.duration, easing: 'linear', fill: 'forwards' }
        );
      }

      this.timer = window.setTimeout(() => this.goTo(this.activeIndex + 1), this.duration);
    }

    syncAutoMotion() {
      // Keep the section guide and the feature carousel on their own configured clocks.
      this.scheduleAutoAdvance();
      (this.carouselStates || []).forEach((state) => this.scheduleCarouselAutoplay(state));
    }

    clearAutoMotion() {
      this.clearTimer();
      this.clearCarouselTimers();
    }

    clearTimer() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
      if (this.progressAnimation) this.progressAnimation.cancel();
      this.progressAnimation = null;
    }

    syncAutoplayControl() {
      if (!this.autoplayToggle) return;

      const hasCarouselAutoplay = (this.carouselStates || []).some(
        (state) => state.autoplayRequested && state.positions.length > 1
      );
      const hasAutoplayVideo = Boolean(this.querySelector('.kl-pdp-story__video video'));
      this.autoplayToggle.hidden =
        (!this.autoplayRequested && !hasCarouselAutoplay && !hasAutoplayVideo) ||
        this.reducedMotion.matches ||
        this.designMode;
    }

    syncVideos() {
      const shouldPlay =
        this.activeIndex === 0 &&
        !this.reducedMotion.matches &&
        !this.userPaused &&
        !document.hidden;

      this.querySelectorAll('video').forEach((video) => {
        const videoWrapper = video.closest('.kl-pdp-story__video');
        const isVisibleSource = videoWrapper && window.getComputedStyle(videoWrapper).display !== 'none';

        if (shouldPlay && isVisibleSource) {
          const playPromise = video.play();
          if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    pauseVideos() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    setupCarousels() {
      this.carousels = Array.from(this.querySelectorAll('[data-kl-carousel]'));
      this.carouselStates = [];

      this.carousels.forEach((carousel) => {
        const track = carousel.querySelector('[data-kl-carousel-track]');
        const slides = Array.from(carousel.querySelectorAll('[data-kl-carousel-slide]'));
        const dots = Array.from(carousel.querySelectorAll('.kl-pdp-story__carousel-dot'));
        if (!track || !slides.length) return;

        const state = {
          carousel,
          track,
          slides,
          dots,
          autoplayRequested: carousel.dataset.klCarouselAutoplay === 'true',
          duration: Math.max(2, Number(carousel.dataset.klCarouselDuration) || 4) * 1000,
          timer: null,
          settleTimer: null,
          updateFrame: null,
          positions: [0],
          panelIndex: this.panels.findIndex((panel) => panel.contains(carousel)),
          focused: false,
          pointerDown: false,
          pointerHoldKey: `carousel-${this.carouselStates.length}-pointer`,
        };
        this.carouselStates.push(state);
        carousel.klCarouselState = state;

        const requestStateUpdate = () => {
          this.clearCarouselTimer(state);
          this.queueCarouselSettle(state);
          if (state.updateFrame) return;
          state.updateFrame = window.requestAnimationFrame(() => {
            state.updateFrame = null;
            this.updateCarouselState(state);
          });
        };

        track.addEventListener('scroll', requestStateUpdate, {
          passive: true,
          signal: this.signal,
        });
        track.addEventListener('dragstart', (event) => event.preventDefault(), {
          signal: this.signal,
        });
        track.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault();
            this.moveCarousel(state, event.key === 'ArrowRight' ? 1 : -1);
          },
          { signal: this.signal }
        );

        carousel.addEventListener(
          'focusin',
          () => {
            state.focused = true;
            this.clearCarouselTimer(state);
          },
          { signal: this.signal }
        );
        carousel.addEventListener(
          'focusout',
          (event) => {
            if (carousel.contains(event.relatedTarget)) return;
            state.focused = false;
            this.scheduleCarouselAutoplay(state);
          },
          { signal: this.signal }
        );

        let dragging = false;
        let startX = 0;
        let startScroll = 0;

        track.addEventListener(
          'pointerdown',
          (event) => {
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            state.pointerDown = true;
            this.interactiveHolds.add(state.pointerHoldKey);
            this.clearAutoMotion();
            if (event.pointerType !== 'mouse') return;
            dragging = true;
            startX = event.clientX;
            startScroll = track.scrollLeft;
            track.classList.add('is-dragging');
            track.setPointerCapture(event.pointerId);
          },
          { signal: this.signal }
        );
        track.addEventListener(
          'pointermove',
          (event) => {
            if (!dragging) return;
            track.scrollLeft = startScroll - (event.clientX - startX);
          },
          { signal: this.signal }
        );

        const endInteraction = (event) => {
          if (dragging) {
            dragging = false;
            track.classList.remove('is-dragging');
            if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
            this.refreshCarouselPositions(state);
            this.moveCarouselTo(state, this.carouselIndex(state));
          }

          state.pointerDown = false;
          this.interactiveHolds.delete(state.pointerHoldKey);
          this.scheduleAutoAdvance();
          this.queueCarouselSettle(state);
        };

        track.addEventListener('pointerup', endInteraction, { signal: this.signal });
        track.addEventListener('pointercancel', endInteraction, { signal: this.signal });
        this.refreshCarouselPositions(state);
        this.updateCarouselState(state);
      });
    }

    setupCarouselStates() {
      (this.carouselStates || []).forEach((state) => {
        this.refreshCarouselPositions(state);
        this.updateCarouselState(state);
      });
      this.syncAutoplayControl();
      this.syncAutoMotion();
    }

    refreshCarouselPositions(state) {
      const maxScroll = Math.max(0, state.track.scrollWidth - state.track.clientWidth);
      const slideCount = state.slides.length;

      if (maxScroll <= 0 || slideCount <= 1) {
        state.positions = [0];
        return;
      }

      // Keep one reachable carousel position per feature, even when multiple cards
      // are visible at once and the last slide cannot align with the track edge.
      state.positions = state.slides.map(
        (_slide, index) => (maxScroll * index) / (slideCount - 1)
      );
    }

    carouselIndex(state) {
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      state.positions.forEach((position, index) => {
        const distance = Math.abs(state.track.scrollLeft - position);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    moveCarouselTo(state, index) {
      const boundedIndex = Math.min(state.positions.length - 1, Math.max(0, index));
      state.track.scrollTo({
        left: state.positions[boundedIndex],
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
      this.queueCarouselSettle(state);
    }

    moveCarousel(state, direction) {
      this.clearCarouselTimer(state);
      this.refreshCarouselPositions(state);
      this.moveCarouselTo(state, this.carouselIndex(state) + direction);
    }

    canAutoplayCarousel(state) {
      return (
        state.autoplayRequested &&
        state.positions.length > 1 &&
        state.panelIndex === this.activeIndex &&
        !this.designMode &&
        !this.reducedMotion.matches &&
        !this.userPaused &&
        !this.hasInteractiveHold() &&
        !state.focused &&
        !state.pointerDown &&
        !document.hidden &&
        document.hasFocus()
      );
    }

    scheduleCarouselAutoplay(state) {
      this.clearCarouselTimer(state);
      window.clearTimeout(state.settleTimer);
      state.settleTimer = null;
      this.refreshCarouselPositions(state);
      this.updateCarouselState(state);

      if (!this.canAutoplayCarousel(state)) return;

      state.timer = window.setTimeout(() => {
        state.timer = null;
        this.refreshCarouselPositions(state);
        if (!this.canAutoplayCarousel(state)) return;

        const nextIndex = (this.carouselIndex(state) + 1) % state.positions.length;
        this.moveCarouselTo(state, nextIndex);
      }, state.duration);
    }

    queueCarouselSettle(state) {
      window.clearTimeout(state.settleTimer);
      state.settleTimer = window.setTimeout(() => {
        state.settleTimer = null;
        this.refreshCarouselPositions(state);
        this.updateCarouselState(state);
        this.scheduleCarouselAutoplay(state);
      }, 240);
    }

    clearCarouselTimer(state) {
      if (state.timer) window.clearTimeout(state.timer);
      state.timer = null;
    }

    clearCarouselTimers() {
      (this.carouselStates || []).forEach((state) => {
        this.clearCarouselTimer(state);
        if (state.settleTimer) window.clearTimeout(state.settleTimer);
        state.settleTimer = null;
        if (state.updateFrame) window.cancelAnimationFrame(state.updateFrame);
        state.updateFrame = null;
      });
    }

    updateCarouselState(state) {
      if (!state.positions.length) this.refreshCarouselPositions(state);
      const index = this.carouselIndex(state);

      state.dots.forEach((dot, dotIndex) => {
        dot.hidden = dotIndex >= state.positions.length;
        dot.classList.toggle('is-active', dotIndex === index);
      });
    }
  }

  customElements.define(elementName, KlPdpStory);
})();
